//// Application CSS

// Carbon Components Styling
import './index.scss'

// Application Functional Styling
import './index.css'

// FLDOH Theme Styling
import './fldoh.css'

//// Application Packages
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider, useSelector } from 'react-redux'
import { store, persistor } from './store'
import {
    createBrowserRouter,
    RouterProvider,
    Navigate,
    useSearchParams,
    useLocation,
} from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'

//// Application Slices
import { selectAuthorizations } from './reducers/authorizationSlice'
import { selectAccount } from './reducers/accountSlice'
import { selectForms } from './reducers/formSlice'

//// Application Components
import Page from './pages'
import LoadingPage from './pages/system-pages/loading-page'
import RegistrationPage from './pages/account-pages/registration-page'
import LoginPage from './pages/account-pages/login-page'
import TemporaryPasswordPage from './pages/account-pages/temporary-password-page'
import ResetPasswordPage from './pages/account-pages/reset-password-page'
import ForgotUserIdPage from './pages/account-pages/forgot-user-id-page'
import FeeAndSummaryPage from './pages/account-pages/fee-and-summary-report-page'

//// Application Pages
// import RegisteredNursingByEndorsementPage from './pages/application-pages/registered-nurse-by-endorsement-page'
import RegisteredNursingByExaminationPage from './pages/application-pages/registered-nurse-by-examination-page'
import LicensedPracticalNursingByExaminationPage from './pages/application-pages/licensed-practical-nurse-by-examination-page'
// import LicensedPracticalNursingByEndorsementPage from './pages/application-pages/licensed-practical-nurse-by-endorsement-page'

//// Migrated Application Pages
import MigratedApplicationMessage from './pages/application-pages/migrated-application-page'

//// Carbon Components

//// Carbon Icons

//// Static Constants

//////// Component

//////// Hooks

//////// State

//////// Slices

//// Slice Data

//////// Search Params

//////// Methods

// Check Page Or Form Is Authenticated
const authenticatedPageOrForm = () => {
    if (process.env.REACT_APP_REDIRECT_ENABLED === 'true') {
        const authorizationsData = useSelector(selectAuthorizations)
        if (authorizationsData?.['tokens']?.['cookie']) {
            // Is There A Cookie Token
            return true
        } else {
            return false
        }
    } else {
        return true
    }
}

// Check Page Is Authorized
const authorizedPage = () => {
    if (process.env.REACT_APP_REDIRECT_ENABLED === 'true') {
        const location = useLocation()
        const { pathname } = location
        const [searchParams] = useSearchParams()
        const categorySearchParam = searchParams.get('category')
        const pageSearchParam = searchParams.get('page')
        const formSearchParam = searchParams.get('form')
        const clientSearchParam = searchParams.get('client')
        const authorizationsData = useSelector(selectAuthorizations)
        const accountData = useSelector(selectAccount)
        const formsData = useSelector(selectForms)
        if (
            authorizationsData?.['tokens']?.['form'] &&
            !pathname.includes('fee-and-summary-report') &&
            !pathname.includes('temporary-password')
        ) {
            // Is There A Form Token & Is This A Regular Page
            return true
        } else if (
            authorizationsData?.['tokens']?.['form'] &&
            pathname.includes('fee-and-summary-report') &&
            formsData?.['summary-submission-form']?.['system']?.['valid']
        ) {
            // Is There A Form Token & Is This The Fee And Summary Report Page & Is The Submission Valid
            return true
        } else if (
            authorizationsData?.['tokens']?.['form'] &&
            pathname.includes('temporary-password') &&
            accountData?.['login']?.['values']?.['login_status'] === 'T'
        ) {
            // Is There A Form Token & Is This The Temporary Password Page & Is The Login Status Temporary
            return true
        } else if (
            categorySearchParam ||
            pageSearchParam ||
            formSearchParam ||
            clientSearchParam
        ) {
            // Is There A Deep Link
            return true
        } else {
            return false
        }
    } else {
        return true
    }
}

// Check Form Is Authorized
const authorizedForm = () => {
    if (process.env.REACT_APP_REDIRECT_ENABLED === 'true') {
        const authorizationsData = useSelector(selectAuthorizations)
        const location = useLocation()
        const { pathname } = location
        // Is There A Form & Does The Form Match The Path
        if (
            authorizationsData?.['tokens']?.['form'] &&
            pathname.includes(authorizationsData?.['tokens']?.['form'])
        ) {
            return true
        } else {
            return false
        }
    } else {
        return true
    }
}

// Protected Routing For Authorized Page
const ProtectedPageRoute = ({ children }) => {
    if (process.env.REACT_APP_REDIRECT_ENABLED === 'true') {
        const location = useLocation()
        const { pathname } = location
        const authenticated = authenticatedPageOrForm()
        const authorized = authorizedPage()
        if (
            (authenticated && authorized && pathname === '/') ||
            (authenticated && authorized && pathname === '/account')
        ) {
            return <Navigate to="/account/login" replace />
        } else if (authenticated && authorized) {
            return children
        } else if (
            (authorized && pathname === '/') ||
            (authorized && pathname === '/account')
        ) {
            return <Navigate to="/account/login" replace />
        } else if (authorized) {
            return children
        } else {
            return window.location.replace(
                `${process.env.REACT_APP_EXTERNAL_REDIRECT_URL}`
            )
        }
    } else {
        return children
    }
}

// Protected Routing For Authorized Form
const ProtectedFormRoute = ({ children }) => {
    if (process.env.REACT_APP_REDIRECT_ENABLED === 'true') {
        const location = useLocation()
        const { pathname } = location
        const authenticated = authenticatedPageOrForm()
        const authorized = authorizedForm()
        const authorizationsData = useSelector(selectAuthorizations)
        if (
            (authenticated && authorized && pathname === '/') ||
            (authenticated && authorized && pathname === '/account')
        ) {
            return (
                <Navigate
                    to={'/' + `${authorizationsData?.tokens?.form}`}
                    replace
                />
            )
        } else if (authenticated && authorized) {
            return children
        } else if (
            (authorized && pathname === '/') ||
            (authorized && pathname === '/account')
        ) {
            return <Navigate to="/account/login" replace />
        } else if (authorized) {
            return <Navigate to="/account/login" replace />
        } else {
            return window.location.replace(
                `${process.env.REACT_APP_EXTERNAL_REDIRECT_URL}`
            )
        }
    } else {
        return children
    }
}

//////// Functional Methods

//////// Memoization

//////// Effects/Side Effects

const router = createBrowserRouter([
    {
        path: '/',
        element: (
            <ProtectedPageRoute>
                <Page>
                    <LoadingPage />
                </Page>
            </ProtectedPageRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/account',
        element: (
            <ProtectedPageRoute>
                <Page>
                    <LoadingPage />
                </Page>
            </ProtectedPageRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/account/registration',
        element: (
            <ProtectedPageRoute>
                <Page>
                    <RegistrationPage />
                </Page>
            </ProtectedPageRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/account/login',
        element: (
            <ProtectedPageRoute>
                <Page>
                    <LoginPage />
                </Page>
            </ProtectedPageRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/account/reset-password',
        element: (
            <Page>
                <ResetPasswordPage />
            </Page>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/account/temporary-password',
        element: (
            <ProtectedPageRoute>
                <Page>
                    <TemporaryPasswordPage />
                </Page>
            </ProtectedPageRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/account/forgot-user-id',
        element: (
            <ProtectedPageRoute>
                <Page>
                    <ForgotUserIdPage />
                </Page>
            </ProtectedPageRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/account/fee-and-summary-report',
        element: (
            <ProtectedPageRoute>
                <Page>
                    <FeeAndSummaryPage />
                </Page>
            </ProtectedPageRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-endorsement',
        element: <MigratedApplicationMessage />,
    },
    {
        path: '/licensed-practical-nurse-by-endorsement',
        element: <MigratedApplicationMessage />,
    },
    // {
    //     path: '/registered-nurse-by-endorsement',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/function-suitability',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/get-started',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/name-and-personal-details',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/contact-information',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/applicant-background',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/other-state-licenses',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/availability-for-disaster',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/education-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/examination-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/health-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/discipline-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/criminal-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/sealed-criminal-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/criminal-and-medicaid-medicare-fraud-questions',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/electronic-fingerprinting',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/florida-center-for-nursing',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/nurse-licensure-compact',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/military-fee-waiver',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/file-attachments',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/attestation',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/registered-nurse-by-endorsement/summary-submission-form',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <RegisteredNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    {
        path: '/registered-nurse-by-examination',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/function-suitability',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/get-started',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/name-and-personal-details',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/contact-information',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/special-testing-accommodations',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/applicant-background',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/other-state-licenses',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/availability-for-disaster',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/education-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/non-us-graduates',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/examination-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/health-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/discipline-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/criminal-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/sealed-criminal-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/criminal-and-medicaid-medicare-fraud-questions',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/electronic-fingerprinting',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/florida-center-for-nursing',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/nurse-licensure-compact',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/military-fee-waiver',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/file-attachments',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/attestation',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/registered-nurse-by-examination/summary-submission-form',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <RegisteredNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/function-suitability',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/get-started',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/name-and-personal-details',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/contact-information',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/special-testing-accommodations',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/applicant-background',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/other-state-licenses',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/availability-for-disaster',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/education-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/practical-nurse-equivalency',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/non-us-graduates',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/examination-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/health-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/discipline-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/criminal-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/sealed-criminal-history',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/criminal-and-medicaid-medicare-fraud-questions',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/electronic-fingerprinting',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/florida-center-for-nursing',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/nurse-licensure-compact',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/military-fee-waiver',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/file-attachments',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/attestation',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    {
        path: '/licensed-practical-nurse-by-examination/summary-submission-form',
        element: (
            <ProtectedFormRoute>
                <Page>
                    <LicensedPracticalNursingByExaminationPage />
                </Page>
            </ProtectedFormRoute>
        ),
        errorElement: <LoadingPage />,
    },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/function-suitability',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/get-started',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/name-and-personal-details',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/contact-information',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/applicant-background',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/other-state-licenses',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/availability-for-disaster',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/education-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/examination-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/health-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/discipline-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/criminal-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/sealed-criminal-history',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/criminal-and-medicaid-medicare-fraud-questions',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/electronic-fingerprinting',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/florida-center-for-nursing',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/nurse-licensure-compact',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/military-fee-waiver',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/file-attachments',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/attestation',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    // {
    //     path: '/licensed-practical-nurse-by-endorsement/summary-submission-form',
    //     element: (
    //         <ProtectedFormRoute>
    //             <Page>
    //                 <LicensedPracticalNursingByEndorsementPage />
    //             </Page>
    //         </ProtectedFormRoute>
    //     ),
    //     errorElement: <LoadingPage />,
    // },
    {
        path: '*',
        element: (
            <ProtectedPageRoute>
                <Page>
                    <LoadingPage />
                </Page>
            </ProtectedPageRoute>
        ),
        errorElement: <LoadingPage />,
    },
])
const container = document.getElementById('root')
const root = createRoot(container)

//////////////// Render & Return
root.render(
    <React.StrictMode>
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <RouterProvider router={router} />
            </PersistGate>
        </Provider>
    </React.StrictMode>
)
